import { eq, and, isNull, asc, sql, inArray, ne } from 'drizzle-orm';
import { db, runInTransaction } from '@/db/client';
import {
    autoCategorizationRules,
    budgetCategories,
    budgets,
    categories,
    financings,
    installments,
    recurringTransactions,
    subcategories,
    transactions,
} from '@/db/schema';
import { generateId } from '@/lib/id';
import { ExpectedError } from '@/lib/errorUtils';
import { isSeedCatchAllName } from '@/lib/seedCategoryNames';
import i18n from '@/lib/i18n';

export type Category = typeof categories.$inferSelect;
export type Subcategory = typeof subcategories.$inferSelect;
export type CategoryWithSubs = Category & { subcategories: Subcategory[] };

function normalizeSubcategoryName(name: string) {
    return name.trim().toLocaleLowerCase();
}

async function findDuplicateActiveSubcategory(categoryId: string, name: string, excludeId?: string) {
    const existing = await db
        .select()
        .from(subcategories)
        .where(
            and(
                eq(subcategories.categoryId, categoryId),
                isNull(subcategories.deletedAt),
            ),
        );

    const normalizedTarget = normalizeSubcategoryName(name);
    return existing.find((subcategory) =>
        subcategory.id !== excludeId
        && normalizeSubcategoryName(subcategory.name) === normalizedTarget,
    );
}

export async function getCategories(
    type?: 'expense' | 'income',
    includeArchived = false,
): Promise<Category[]> {
    const conditions = [isNull(categories.deletedAt)];
    if (type) conditions.push(eq(categories.type, type));
    if (!includeArchived) conditions.push(eq(categories.isArchived, 0));

    return db
        .select()
        .from(categories)
        .where(and(...conditions))
        .orderBy(asc(categories.sortOrder), asc(categories.name));
}

export async function getCustomCategoryCount(): Promise<number> {
    const [result] = await db
        .select({ count: sql<number>`COUNT(*)` })
        .from(categories)
        .where(
            and(
                eq(categories.isSystem, 0),
                eq(categories.isArchived, 0),
                isNull(categories.deletedAt),
            ),
        );

    return result?.count ?? 0;
}

export async function getCategoriesWithSubcategories(
    type?: 'expense' | 'income',
    includeArchived = false,
): Promise<CategoryWithSubs[]> {
    const cats = await getCategories(type, includeArchived);
    if (cats.length === 0) return [];

    const subConditions = [isNull(subcategories.deletedAt)];
    if (!includeArchived) subConditions.push(eq(subcategories.isArchived, 0));

    const subs = await db
        .select()
        .from(subcategories)
        .where(and(...subConditions))
        .orderBy(asc(subcategories.sortOrder), asc(subcategories.name));

    const subsByCategory = new Map<string, Subcategory[]>();
    for (const sub of subs) {
        const list = subsByCategory.get(sub.categoryId) ?? [];
        list.push(sub);
        subsByCategory.set(sub.categoryId, list);
    }

    return cats.map((cat) => ({
        ...cat,
        subcategories: subsByCategory.get(cat.id) ?? [],
    }));
}

export async function getCategoryById(id: string): Promise<Category | null> {
    const results = await db
        .select()
        .from(categories)
        .where(and(eq(categories.id, id), isNull(categories.deletedAt)));
    // null (não undefined): é a queryFn de ['categories','detail',id] e o React Query proíbe
    // undefined. Sem isto, mesclar/excluir a categoria (que a soft-deleta) fazia o refetch da
    // tela de detalhe ainda montada estourar "Query data cannot be undefined".
    return results[0] ?? null;
}

export async function getSubcategoriesByCategoryId(categoryId: string): Promise<Subcategory[]> {
    return db
        .select()
        .from(subcategories)
        .where(
            and(
                eq(subcategories.categoryId, categoryId),
                isNull(subcategories.deletedAt),
                eq(subcategories.isArchived, 0),
            ),
        )
        .orderBy(asc(subcategories.sortOrder), asc(subcategories.name));
}

/** Categoria de receita usada nos depósitos de meta "somar ao saldo". */
const GOAL_DEPOSIT_CATEGORY = { name: 'Metas', icon: 'target', color: '#0D9488' } as const;

/**
 * Categoria "Metas" (receita) para depósitos de meta que somam ao saldo,
 * criada sob demanda na primeira vez. Respeita a vontade do usuário: se ele
 * arquivou ou excluiu (merge) a categoria, NÃO recria — retorna null e o
 * depósito sai sem categoria.
 */
export async function findOrCreateGoalDepositCategoryId(): Promise<string | null> {
    const rows = await db
        .select({
            id: categories.id,
            isArchived: categories.isArchived,
            deletedAt: categories.deletedAt,
        })
        .from(categories)
        .where(and(
            eq(categories.type, 'income'),
            sql`LOWER(${categories.name}) = LOWER(${GOAL_DEPOSIT_CATEGORY.name})`,
        ));

    if (rows.length > 0) {
        const active = rows.find((row) => !row.deletedAt && !row.isArchived);
        return active?.id ?? null;
    }

    const id = generateId();
    const now = new Date().toISOString();
    await db.insert(categories).values({
        id,
        name: GOAL_DEPOSIT_CATEGORY.name,
        type: 'income',
        icon: GOAL_DEPOSIT_CATEGORY.icon,
        color: GOAL_DEPOSIT_CATEGORY.color,
        isSystem: 1,
        isArchived: 0,
        sortOrder: 999,
        createdAt: now,
        updatedAt: now,
    });
    return id;
}

export async function createCategory(data: {
    name: string;
    type: 'expense' | 'income';
    icon: string;
    color: string;
}): Promise<Category> {
    const id = generateId();
    const now = new Date().toISOString();
    await db.insert(categories).values({
        id,
        ...data,
        isSystem: 0,
        isArchived: 0,
        sortOrder: 999,
        createdAt: now,
        updatedAt: now,
    });
    return (await getCategoryById(id))!;
}

export async function updateCategory(
    id: string,
    data: Partial<{ name: string; icon: string; color: string }>,
): Promise<void> {
    await db
        .update(categories)
        .set({ ...data, updatedAt: new Date().toISOString() })
        .where(eq(categories.id, id));
}

export async function archiveCategory(id: string): Promise<void> {
    await db
        .update(categories)
        .set({ isArchived: 1, updatedAt: new Date().toISOString() })
        .where(eq(categories.id, id));
}

export async function unarchiveCategory(id: string): Promise<void> {
    await db
        .update(categories)
        .set({ isArchived: 0, updatedAt: new Date().toISOString() })
        .where(eq(categories.id, id));
}

export async function reorderCategories(orderedIds: string[]): Promise<void> {
    const now = new Date().toISOString();
    for (let i = 0; i < orderedIds.length; i++) {
        await db
            .update(categories)
            .set({ sortOrder: i, updatedAt: now })
            .where(eq(categories.id, orderedIds[i]));
    }
}

/** Mesma coisa para as subcategorias de UMA categoria (as consultas já ordenam por sortOrder). */
export async function reorderSubcategories(orderedIds: string[]): Promise<void> {
    const now = new Date().toISOString();
    for (let i = 0; i < orderedIds.length; i++) {
        await db
            .update(subcategories)
            .set({ sortOrder: i, updatedAt: now })
            .where(eq(subcategories.id, orderedIds[i]));
    }
}

export async function createSubcategory(data: {
    categoryId: string;
    name: string;
    icon?: string;
}): Promise<Subcategory> {
    const normalizedName = data.name.trim();
    const duplicate = await findDuplicateActiveSubcategory(data.categoryId, normalizedName);
    if (duplicate) {
        throw new ExpectedError(i18n.t('ui:errors.subcategoryNameTaken'));
    }

    const id = generateId();
    const now = new Date().toISOString();
    await db.insert(subcategories).values({
        id,
        categoryId: data.categoryId,
        name: normalizedName,
        icon: data.icon ?? null,
        isSystem: 0,
        isArchived: 0,
        sortOrder: 999,
        createdAt: now,
        updatedAt: now,
    });
    const results = await db
        .select()
        .from(subcategories)
        .where(eq(subcategories.id, id));
    return results[0];
}

export async function updateSubcategory(
    id: string,
    data: Partial<{ name: string; icon: string }>,
): Promise<void> {
    if (data.name !== undefined) {
        const [existing] = await db
            .select({
                categoryId: subcategories.categoryId,
            })
            .from(subcategories)
            .where(eq(subcategories.id, id));

        if (!existing) {
            throw new Error('Subcategoria não encontrada.');
        }

        const normalizedName = data.name.trim();
        const duplicate = await findDuplicateActiveSubcategory(existing.categoryId, normalizedName, id);
        if (duplicate) {
            throw new ExpectedError(i18n.t('ui:errors.subcategoryNameTaken'));
        }

        data = { ...data, name: normalizedName };
    }

    await db
        .update(subcategories)
        .set({ ...data, updatedAt: new Date().toISOString() })
        .where(eq(subcategories.id, id));
}

export async function archiveSubcategory(id: string): Promise<void> {
    await db
        .update(subcategories)
        .set({ isArchived: 1, updatedAt: new Date().toISOString() })
        .where(eq(subcategories.id, id));
}

/**
 * Apagar a subcategoria DESFAZ a classificação em tudo que apontava pra ela — a categoria
 * fica, a subcategoria some. Vale para o que já está lançado e, principalmente, para o que
 * ainda vai nascer: recorrência, parcelamento e financiamento carimbam a subcategoria em
 * cada nova ocorrência, e os joins de leitura não filtram `deletedAt`, então uma delas
 * esquecida faz o nome apagado REAPARECER nos lançamentos futuros.
 */
export async function deleteSubcategory(id: string): Promise<void> {
    const now = new Date().toISOString();
    await db
        .update(transactions)
        .set({ subcategoryId: null, updatedAt: now })
        .where(eq(transactions.subcategoryId, id));
    // Fontes que ainda vão gerar lançamento.
    await db
        .update(recurringTransactions)
        .set({ subcategoryId: null, updatedAt: now })
        .where(eq(recurringTransactions.subcategoryId, id));
    await db
        .update(installments)
        .set({ subcategoryId: null, updatedAt: now })
        .where(eq(installments.subcategoryId, id));
    await db
        .update(financings)
        .set({ subcategoryId: null, updatedAt: now })
        .where(eq(financings.subcategoryId, id));
    // Keep automatic rules, but stop them from targeting an invisible subcategory
    await db
        .update(autoCategorizationRules)
        .set({ subcategoryId: null })
        .where(eq(autoCategorizationRules.subcategoryId, id));
    // Soft-delete the subcategory
    await db
        .update(subcategories)
        .set({ deletedAt: now, updatedAt: now })
        .where(eq(subcategories.id, id));
}

export async function mergeCategories(sourceId: string, targetId: string): Promise<number> {
    const now = new Date().toISOString();

    // Count affected transactions
    const [countResult] = await db
        .select({ count: sql<number>`COUNT(*)` })
        .from(transactions)
        .where(and(eq(transactions.categoryId, sourceId), isNull(transactions.deletedAt)));
    const txCount = countResult?.count ?? 0;

    // Move all transactions from source to target
    await db
        .update(transactions)
        .set({ categoryId: targetId, updatedAt: now })
        .where(eq(transactions.categoryId, sourceId));

    // Move subcategories from source to target
    await db
        .update(subcategories)
        .set({ categoryId: targetId, updatedAt: now })
        .where(eq(subcategories.categoryId, sourceId));

    // Keep learned/default rules useful after a category merge
    await db
        .update(autoCategorizationRules)
        .set({ categoryId: targetId })
        .where(eq(autoCategorizationRules.categoryId, sourceId));

    // Repoint budget memberships: orçamentos que já incluem o destino mantêm o deles e
    // descartam o link da origem (evita duplicata); os demais são repontados pro destino.
    // Sem isso, o link ficava órfão apontando pra categoria soft-deleted (a query de
    // orçamento não filtra deletedAt → a categoria-fantasma aparecia e perdia o gasto).
    const targetBudgetRows = await db
        .select({ budgetId: budgetCategories.budgetId })
        .from(budgetCategories)
        .where(eq(budgetCategories.categoryId, targetId));
    const targetBudgetIds = targetBudgetRows.map((row) => row.budgetId);
    if (targetBudgetIds.length > 0) {
        await db
            .delete(budgetCategories)
            .where(and(
                eq(budgetCategories.categoryId, sourceId),
                inArray(budgetCategories.budgetId, targetBudgetIds),
            ));
    }
    await db
        .update(budgetCategories)
        .set({ categoryId: targetId })
        .where(eq(budgetCategories.categoryId, sourceId));

    // Soft-delete the source category
    await db
        .update(categories)
        .set({ deletedAt: now, updatedAt: now })
        .where(eq(categories.id, sourceId));

    return txCount;
}

/** Active transactions currently using this category (drives the delete warning). */
export async function getCategoryTransactionCount(id: string): Promise<number> {
    const [result] = await db
        .select({ count: sql<number>`COUNT(*)` })
        .from(transactions)
        .where(and(eq(transactions.categoryId, id), isNull(transactions.deletedAt)));
    return result?.count ?? 0;
}

/**
 * Same-type category to absorb a deleted category's data — prefers the seed
 * catch-all ("Outros" no seed pt-BR, "Other" no seed en). Ver lib/seedCategoryNames:
 * o seed em inglês chama de "Other" (singular), então o casamento antigo por
 * 'outros'/'others' errava e despejava os lançamentos na PRIMEIRA categoria do tipo.
 */
async function findFallbackCategory(
    type: string,
    excludeId: string,
): Promise<{ id: string; name: string } | null> {
    const candidates = await db
        .select({ id: categories.id, name: categories.name })
        .from(categories)
        .where(and(
            eq(categories.type, type as 'expense' | 'income'),
            isNull(categories.deletedAt),
            eq(categories.isArchived, 0),
            ne(categories.id, excludeId),
        ));
    const catchAll = candidates.find((c) => isSeedCatchAllName(c.name));
    return catchAll ?? candidates[0] ?? null;
}

/**
 * Nome da categoria que absorveria os dados se esta for apagada — pro dialog de
 * exclusão citar o destino REAL (não um "Outros" hardcoded que pode nem existir).
 */
export async function getDeleteFallbackCategoryName(id: string): Promise<string | null> {
    const [category] = await db.select().from(categories).where(eq(categories.id, id));
    if (!category) return null;
    return (await findFallbackCategory(category.type, id))?.name ?? null;
}

/**
 * Deletes a category, reassigning everything that referenced it to the same-type
 * "Outros" category (or any other same-type category if "Outros" is gone). It is
 * a merge with an auto-chosen destination — except the deleted category's own
 * subcategories are dropped, so the moved rows lose them (a merge keeps them).
 *
 * The seed never resurrects a soft-deleted top-level category: the full seed runs
 * only on an empty DB, and the incremental pass skips categories with `deletedAt`
 * (see db/seed.ts), so a deleted category stays deleted across launches.
 *
 * Throws when there's no other same-type category to absorb the data. Returns the
 * number of transactions that were moved.
 */
export async function deleteCategory(id: string): Promise<number> {
    const [category] = await db.select().from(categories).where(eq(categories.id, id));
    if (!category) return 0;

    const targetId = (await findFallbackCategory(category.type, id))?.id ?? null;
    if (!targetId) {
        // Reapontar manda lançamentos VIVOS pro destino — não faz sentido despejá-los
        // num balde arquivado ("não me ofereça mais isto"). Se a única outra do mesmo
        // tipo está arquivada, reativá-la (ou criar outra ativa) destrava. Ver ADR 0016.
        const archivedSameType = await db
            .select({ id: categories.id })
            .from(categories)
            .where(and(
                eq(categories.type, category.type),
                isNull(categories.deletedAt),
                eq(categories.isArchived, 1),
                ne(categories.id, id),
            ))
            .limit(1);
        throw new ExpectedError(archivedSameType.length > 0
            ? i18n.t('ui:errors.categoryDeleteOnlyArchivedLeft')
            : i18n.t('ui:errors.categoryDeleteNeedsAnother'));
    }

    const now = new Date().toISOString();
    const txCount = await getCategoryTransactionCount(id);

    // Tudo numa transação: reapontar as referências e fazer o soft-delete precisa ser
    // atômico — uma falha no meio deixaria lançamentos já remapeados com a categoria
    // ainda viva (estado inconsistente, sem rollback). Mesmo motivo do clearDatabase.
    await runInTransaction(async () => {
        const tx = db;
        // Reassign every reference to the fallback. Subcategory links are cleared
        // because this category's subcategories are being deleted along with it.
        await tx.update(transactions)
            .set({ categoryId: targetId, subcategoryId: null, updatedAt: now })
            .where(eq(transactions.categoryId, id));
        await tx.update(recurringTransactions)
            .set({ categoryId: targetId, subcategoryId: null, updatedAt: now })
            .where(eq(recurringTransactions.categoryId, id));
        await tx.update(installments)
            .set({ categoryId: targetId, subcategoryId: null, updatedAt: now })
            .where(eq(installments.categoryId, id));
        await tx.update(financings)
            .set({ categoryId: targetId, subcategoryId: null, updatedAt: now })
            .where(eq(financings.categoryId, id));
        await tx.update(autoCategorizationRules)
            .set({ categoryId: targetId, subcategoryId: null })
            .where(eq(autoCategorizationRules.categoryId, id));

        // Budgets: legacy "main category" pointer + memberships (dedup like a merge).
        await tx.update(budgets)
            .set({ categoryId: targetId, updatedAt: now })
            .where(eq(budgets.categoryId, id));
        const targetBudgetRows = await tx
            .select({ budgetId: budgetCategories.budgetId })
            .from(budgetCategories)
            .where(eq(budgetCategories.categoryId, targetId));
        const targetBudgetIds = targetBudgetRows.map((row) => row.budgetId);
        if (targetBudgetIds.length > 0) {
            await tx.delete(budgetCategories)
                .where(and(
                    eq(budgetCategories.categoryId, id),
                    inArray(budgetCategories.budgetId, targetBudgetIds),
                ));
        }
        await tx.update(budgetCategories)
            .set({ categoryId: targetId })
            .where(eq(budgetCategories.categoryId, id));

        // Drop this category's subcategories, then the category itself.
        await tx.update(subcategories)
            .set({ deletedAt: now, updatedAt: now })
            .where(and(eq(subcategories.categoryId, id), isNull(subcategories.deletedAt)));
        await tx.update(categories)
            .set({ deletedAt: now, updatedAt: now })
            .where(eq(categories.id, id));
    });

    return txCount;
}
